import React, { useState, useCallback } from "react";
import { pdf } from "@react-pdf/renderer";
import { toast } from "sonner";
import { Download, Save, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import FlyerForm from "../components/flyer/FlyerForm";
import FlyerPreview from "../components/flyer/FlyerPreview";
import FlyerDocument from "../components/flyer/FlyerDocument";
import { defaultFlyer } from "../lib/defaultFlyer";
import { saveFlyer } from "../lib/api";

const LS_KEY = "messypups_flyer_draft_v1";
const LS_ID_KEY = "messypups_flyer_id_v1";

const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const FlyerEditor = () => {
  const [flyer, setFlyer] = useState(() => loadFromStorage() || defaultFlyer);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(true);

  // Persist on every change.
  React.useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(flyer));
  }, [flyer]);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const blob = await pdf(<FlyerDocument flyer={flyer} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (flyer.name || "messy-pups-flyer")
        .replace(/[^a-z0-9-_ ]/gi, "")
        .replace(/\s+/g, "-")
        .toLowerCase();
      a.download = `${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Flyer PDF downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  }, [flyer]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const existingId = localStorage.getItem(LS_ID_KEY);
      const saved = await saveFlyer(existingId, {
        name: flyer.name,
        data: flyer,
      });
      localStorage.setItem(LS_ID_KEY, saved.id);
      toast.success("Saved to cloud");
    } catch (e) {
      console.error(e);
      toast.error("Save failed — your draft is still safe locally");
    } finally {
      setSaving(false);
    }
  }, [flyer]);

  const handleReset = useCallback(() => {
    setFlyer(defaultFlyer);
    toast.message("Reset to default Messy Pups content");
  }, []);

  return (
    <div
      className="min-h-screen bg-[#FAF7F1] text-[#0C2A20]"
      data-testid="flyer-editor-page"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#FAF7F1]/85 backdrop-blur border-b border-stone-200">
        <div className="max-w-[1440px] mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="topbar-brand">
            <img
              src="/brand/logo-circle.png"
              alt="Messy Pups"
              className="w-10 h-10 rounded-full"
            />
            <div>
              <h1
                className="text-xl text-[#114232] leading-none"
                style={{ fontFamily: "Chewy, system-ui" }}
              >
                Messy Pups Flyer Studio
              </h1>
              <p className="text-[11px] text-stone-500 tracking-wide uppercase">
                Build · Edit · Download A4 PDF
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreviewOpen((v) => !v)}
              data-testid="toggle-preview-btn"
              className="border-[#94A596] text-[#114232]"
            >
              {previewOpen ? (
                <>
                  <EyeOff className="w-4 h-4 mr-1.5" /> Hide preview
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-1.5" /> Show preview
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-flyer-btn"
              className="border-[#94A596] text-[#114232]"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1.5" />
              )}
              Save
            </Button>
            <Button
              size="sm"
              onClick={handleDownload}
              disabled={downloading}
              data-testid="download-pdf-btn"
              className="bg-[#114232] hover:bg-[#0C2A20] text-[#FAF7F1]"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-1.5" />
              )}
              Download PDF
            </Button>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-[1440px] mx-auto px-5 py-6 grid gap-6 lg:grid-cols-[420px_1fr]">
        {/* Preview column — shows FIRST on mobile so the user immediately sees the flyer, then second on lg+ */}
        {previewOpen && (
          <main
            className="order-1 lg:order-2 lg:sticky lg:top-[80px] h-[60vh] lg:h-[calc(100vh-110px)]"
            data-testid="preview-column"
          >
            <FlyerPreview flyer={flyer} />
          </main>
        )}

        {/* Form column */}
        <aside
          className="order-2 lg:order-1 lg:max-h-[calc(100vh-110px)] lg:overflow-y-auto lg:pr-2"
          data-testid="form-column"
        >
          <FlyerForm
            flyer={flyer}
            setFlyer={setFlyer}
            onReset={handleReset}
          />
        </aside>
      </div>
    </div>
  );
};

export default FlyerEditor;
